import { stripAnsi } from "./parseOutputs";

/**
 * Parse the fixed-width table that `paperclip sql` prints:
 *
 *   col_a  | col_b
 *   -------+------
 *   value  | 42
 *   (1 rows, 7ms)
 *
 * Column boundaries come from the `+` positions in the separator line, so
 * values containing `|` don't break parsing. `NULL` and empty cells become
 * null. Returns [] when no table is found (e.g. "(0 rows)" or an error).
 */
export const parseSqlTable = (
  output: string,
): Array<Record<string, string | null>> => {
  const lines = stripAnsi(output).split("\n");
  const sepIdx = lines.findIndex((line) => /^-+(\+-+)+\s*$/.test(line));
  if (sepIdx < 1) return [];

  const separator = lines[sepIdx];
  const boundaries: number[] = [];
  for (let i = 0; i < separator.length; i++) {
    if (separator[i] === "+") boundaries.push(i);
  }

  const sliceCells = (line: string): string[] => {
    const cells: string[] = [];
    let start = 0;
    for (const b of boundaries) {
      cells.push(line.slice(start, b).trim());
      start = b + 1;
    }
    cells.push(line.slice(start).trim());
    return cells;
  };

  const headers = sliceCells(lines[sepIdx - 1]);
  const rows: Array<Record<string, string | null>> = [];
  for (let i = sepIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim() || /^\(\d+ rows?/.test(line.trim())) break;
    const cells = sliceCells(line);
    const row: Record<string, string | null> = {};
    headers.forEach((header, col) => {
      const value = cells[col] ?? "";
      row[header] = value === "" || value === "NULL" ? null : value;
    });
    rows.push(row);
  }
  return rows;
};
